<script setup lang="ts">
import { computed, ref } from 'vue'
import { useScreen } from '@/composables/useScreen'
import { currentUser } from '@/lib/currentUser'
import NoGradeCard from './NoGradeCard.vue'

interface Ace {
  name: string
  ace_certificate: string
  ace_link: string
  tutor_certificate?: string
  tutor_link?: string
}

interface Context {
  general: {
    hmac: string
    profile_image_url?: string
    has_video?: boolean
    last_name?: string
    first_name?: string
    patronymic?: string
    is_author?: boolean
    id: number
    is_methodist?: boolean
    languages?: string[]
    [key: string]: unknown
  }
  certificates?: Array<{ aces: Ace[] }>
  [key: string]: unknown
}

const props = defineProps<{
  context: Context
  isCurrentUser: boolean
}>()

const { isMobile } = useScreen()
const teacherVideoModal = ref(false)

const hmac = computed(() => props.context.general.hmac)
const user = computed(() => currentUser)
const badges = computed(() => {
  const aces: Array<{ name: string; key: string; href: string }> = []
  const tutors: Array<{ name: string; key: string; href: string }> = []

  const certs = props.context.certificates ?? []
  for (const cert of certs) {
    for (const ace of cert.aces) {
      aces.push({
        name: ace.name,
        key: ace.ace_certificate,
        href: ace.ace_link
      })

      if (!ace.tutor_certificate || !ace.tutor_link) continue
      tutors.push({
        name: ace.name,
        key: ace.tutor_certificate,
        href: ace.tutor_link
      })
    }
  }

  return { aces, tutors }
})
</script>

<template>
  <div class="header">
    <!-- legacy! -->
    <div class="photo" :style="{ backgroundImage: `url(${context.general.profile_image_url})` }">
      <div
        v-if="context.general.has_video"
        class="open-video teacher-bio__video-btn"
        @click="() => (teacherVideoModal = true)"
      >
        <div class="action">▶</div>
        <div class="caption">Видеовизитка</div>
      </div>
    </div>

    <div class="info flex-spacer full-width">
      <div class="name">
        {{ context.general.last_name }}<br />
        {{ context.general.first_name }} {{ context.general.patronymic }}
      </div>

      <v-link
        v-if="context.general.is_author"
        class="d-flex my-1 align-center"
        :href="`/teacher/author/${context.general.id}`"
      >
        <v-icon size="24" class="mr-1">book-blue</v-icon>
        <span class="text-overline blue-text"> Автор курса </span>
      </v-link>

      <div class="teacher-bio__badges my-2">
        <div
          v-if="badges.aces.length"
          class="teacher-bio__badge teacher-bio__badge_ace"
          data-tooltip="Учитель АС: Учитель высокой квалификации, обладатель Авторского Сертификата (АС)"
        >
          <div v-for="ace in badges.aces" :key="ace.key" class="teacher-bio__badge-item">
            <v-icon size="20">cert-done</v-icon>
            <a class="teacher-bio__badge-tooltip blue-text" :href="ace.href" target="_blank" v-text="ace.name"></a>
          </div>
          АС
        </div>

        <div
          v-if="badges.tutors.length"
          class="teacher-bio__badge teacher-bio__badge_tutor"
          data-tooltip="Учитель АС + Тьютор: Учитель высокой квалификации, обладатель Авторского Сертификата (АС), прошедший Школу Тьюторов и имеющий право в рамках сайта давать консультации по системе I LOVE ENGLISH"
        >
          <div v-for="tutor in badges.tutors" :key="tutor.key" class="teacher-bio__badge-item">
            <v-icon size="20">cert-tutor-done</v-icon>
            <a class="teacher-bio__badge-tooltip blue-text" :href="tutor.href" target="_blank" v-text="tutor.name"></a>
          </div>
          Тьютор
        </div>

        <div
          v-if="context.general.is_methodist"
          class="teacher-bio__badge teacher-bio__badge_methodist"
          data-tooltip="Учитель АС + Методист: Учитель высокой квалификации, обладатель Авторского Сертификата (АС), обучающий учителей методике I LOVE ENGLISH на сайте МирГоворит."
        >
          <v-icon class="mr-1">crown</v-icon>
          Методист
        </div>
      </div>

      <p>
        {{ context.general.languages?.join(', ') }}
      </p>

      <NoGradeCard
        v-if="isCurrentUser && user.hasTeacherPurchase && !user.hasTeacherGrade"
        class="mt-2"
      />
    </div>

    <div v-if="isMobile" class="mt-1"></div>
    <v-link v-if="isCurrentUser" mode="primary" href="/teacher/edit_profile" class="text-nowrap text-body">
      <v-icon size="20" class="mr-1">mdi-pencil</v-icon>
      Редактировать профиль
    </v-link>

    <!-- Video card modal -->
    <v-modal v-model="teacherVideoModal" title="Видеовизитка">
      <video width="100%" height="100%" preload="metadata" controls class="teacher-bio__video-modal">
        <source :src="`/teacher/get_teacher_video_file/${context.general.id}/${hmac}`" type="video/mp4" />
      </video>
    </v-modal>
  </div>
</template>
